import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const SeasonContext = createContext();

export const useSeason = () => useContext(SeasonContext);

// Setup Axios Interceptor Globally
axios.interceptors.request.use((config) => {
  if (config.url && config.url.includes("maize.norissolutions.in/Php/public/api")) {
    const excludeEndpoints = ['/auth/login', '/auth/register', '/auth/change-password', '/auth/refresh', '/seasons'];
    const shouldExclude = excludeEndpoints.some(endpoint => config.url.includes(endpoint));
    const currentSeason = sessionStorage.getItem('maaze_season_id');

    if (!shouldExclude && currentSeason) {
      config.params = { ...config.params, season_id: currentSeason };
    }
  }
  return config;
}, (error) => Promise.reject(error));

export const SeasonProvider = ({ children }) => {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(() => {
    return sessionStorage.getItem('maaze_season_id') || "";
  });
  const [isReady, setIsReady] = useState(false);

  const token = sessionStorage.getItem("maaze_token");

  // Fetch seasons on mount
  useEffect(() => {
    const fetchSeasons = async () => {
      if (!token) return;
      try {
        const res = await axios.get("https://maize.norissolutions.in/Php/public/api/seasons", {
          headers: { Authorization: `Bearer ${token}` }
        });

        const fetchedSeasons = res.data?.data || [];
        setSeasons(fetchedSeasons);

        // Evaluate user for organiser setup
        const userStr = sessionStorage.getItem("maaze_user");
        const user = userStr ? JSON.parse(userStr) : null;
        let autoSeasonId = sessionStorage.getItem('maaze_season_id') || "";

        // If user is an Organiser, always fetch their organiser record and store it
        if (user && user.role === 'Organiser') {
          try {
            const orgRes = await axios.get(
              `https://maize.norissolutions.in/Php/public/api/organisers`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: autoSeasonId ? { season_id: autoSeasonId } : {}
              }
            );

            const rawOrgs = orgRes.data?.data;
            const fetchedOrgs = Array.isArray(rawOrgs) ? rawOrgs : (rawOrgs ? [rawOrgs] : []);

            const myOrg = fetchedOrgs.find(
              (o) => o.user_id == user.id || o.email === user.email
            );

            if (myOrg) {
              sessionStorage.setItem(
                "maaze_organiser",
                JSON.stringify(myOrg)
              );
            }

          } catch (err) {
            console.error("Organiser fetch failed:", err);
          }
        }

        // If no season is selected, try to auto-select
        if (!autoSeasonId && fetchedSeasons.length > 0) {
          // Fallback to active season or first one
          const activeSeason = fetchedSeasons.find(s => s.is_active === 1) || fetchedSeasons[0];
          autoSeasonId = activeSeason.season_id.toString();

          if (autoSeasonId) {
            setSelectedSeason(autoSeasonId);
            sessionStorage.setItem('maaze_season_id', autoSeasonId);
          }
        }
      } catch (err) {
        console.error("Failed to fetch seasons:", err);
      } finally {
        setIsReady(true);
      }
    };

    fetchSeasons();
  }, [token]);

  // Handle selected season change
  const changeSeason = (id) => {
    setSelectedSeason(id);
    sessionStorage.setItem('maaze_season_id', id);
    // Reload page to ensure all components refetch with the new season
    window.location.reload();
  };

  return (
    <SeasonContext.Provider value={{ seasons, selectedSeason, changeSeason }}>
      {!isReady ? (
        <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
          Loading application data...
        </div>
      ) : children}
    </SeasonContext.Provider>
  );
};
